import React, { useEffect, useState, useRef } from 'react';
import { Input } from '../controls/Input';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { validateSnowflakeAccountIdentifier, resetValidation } from '../../reducers/snowflakeAccountsSlice';
import { useToken } from '../../hooks/useToken';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

interface AccountValidationInputProps {
  onChange: (value: string) => void;
  onValidationSuccess?: (accountIdentifier: string) => void;
}

/**
 * Validate account identifier format (basic client-side validation)
 * Accepts formats like: orgname-accountname or account_locator.region
 */
function validateIdentifierFormat(identifier: string): { isValid: boolean; message?: string } {
  const trimmed = identifier.trim();

  if (trimmed.length === 0) {
    return { isValid: false, message: 'Account identifier is required' };
  }

  if (trimmed.length < 3) {
    return { isValid: false, message: 'Account identifier is too short' };
  }

  if (trimmed.length > 255) {
    return { isValid: false, message: 'Account identifier is too long' };
  }

  // Check for invalid characters (only alphanumeric, hyphens, underscores, and dots allowed)
  if (!/^[a-zA-Z0-9._-]+$/.test(trimmed)) {
    return {
      isValid: false,
      message: 'Account identifier can only contain letters, numbers, hyphens, underscores, and dots',
    };
  }

  // Check for common format patterns
  const hasHyphen = trimmed.includes('-');
  const hasDot = trimmed.includes('.');

  if (!hasHyphen && !hasDot) {
    return {
      isValid: false,
      message: 'Account identifier should be in format: orgname-accountname or account.region',
    };
  }

  return { isValid: true };
}

/**
 * Input field with real-time validation for Snowflake account identifiers
 *
 * Features:
 * - Text input for account identifier (format: orgname-accountname)
 * - Client-side format validation
 * - Real-time server-side validation using Redux thunk
 * - Shows loading spinner while validating
 * - Displays validation status (checkmark for valid, error message for invalid)
 * - Debounced validation (500ms after user stops typing)
 *
 * @param onChange - Callback when input value changes
 * @param onValidationSuccess - Optional callback when validation succeeds
 */
export function AccountValidationInput({ onChange, onValidationSuccess }: AccountValidationInputProps) {
  const dispatch = useAppDispatch();
  const token = useToken();
  const validationStatus = useAppSelector((state) => state.snowflakeAccounts.validationStatus);
  const validationMessage = useAppSelector((state) => state.snowflakeAccounts.validationMessage);

  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState(inputValue);
  const [clientError, setClientError] = useState<string | null>(null);

  // Store the callback in a ref to avoid triggering validation on every callback change
  const onValidationSuccessRef = useRef(onValidationSuccess);
  useEffect(() => {
    onValidationSuccessRef.current = onValidationSuccess;
  }, [onValidationSuccess]);

  // Debounce the input value
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [inputValue]);

  // Validate when debounced value changes
  useEffect(() => {
    const trimmed = (debouncedValue || '').trim();

    if (trimmed.length === 0) {
      dispatch(resetValidation());
      setClientError(null);
      return;
    }

    // Client-side format validation first
    const formatValidation = validateIdentifierFormat(trimmed);
    if (!formatValidation.isValid) {
      dispatch(resetValidation());
      setClientError(formatValidation.message ?? 'Invalid format');
      return;
    }

    // Clear client error and proceed with server validation
    setClientError(null);
    dispatch(validateSnowflakeAccountIdentifier({ token, identifier: trimmed }))
      .unwrap()
      .then((result) => {
        if (result.isValid && onValidationSuccessRef.current) {
          onValidationSuccessRef.current(trimmed);
        }
      })
      .catch((error) => {
        console.error('Validation error:', error);
      });
  }, [debouncedValue, dispatch, token]);

  // Reset validation when component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetValidation());
      setClientError(null);
    };
  }, [dispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
  };

  const isValidating = validationStatus === 'validating';
  const isValid = validationStatus === 'valid';
  const isInvalid = validationStatus === 'invalid' || clientError !== null;
  const errorMessage = clientError ?? validationMessage ?? 'Invalid account identifier';

  return (
    <div className="relative">
      <Input
        id="account-identifier"
        label="Snowflake Account Identifier"
        placeholder="orgname-accountname"
        value={inputValue}
        onChange={handleChange}
        loading={isValidating}
        error={isInvalid ? errorMessage : undefined}
        required
        aria-required="true"
        aria-invalid={isInvalid}
        aria-describedby="account-identifier-help"
        help={
          <div id="account-identifier-help" className="space-y-1">
            <p>Enter your Snowflake account identifier in the format: orgname-accountname</p>
            <p className="text-xs">
              You can find this in your Snowflake account URL or by running{' '}
              <code className="bg-white px-1 py-0.5 rounded">SELECT CURRENT_ACCOUNT()</code> in Snowflake.
            </p>
          </div>
        }
      />
      {isValid && (
        <div className="absolute right-3 top-9 flex items-center pointer-events-none" aria-hidden="true">
          <CheckCircleIcon className="h-5 w-5 text-green-600" />
        </div>
      )}
      {isInvalid && (
        <div className="absolute right-3 top-9 flex items-center pointer-events-none" aria-hidden="true">
          <XCircleIcon className="h-5 w-5 text-red-600" />
        </div>
      )}
    </div>
  );
}
